import { apiFetch } from '../lib/apiFetch';

/**
 * One Send-Email prefill template (#20). The catalog is the single source of
 * truth on the backend (backend/email_templates.py); the dialog renders the
 * dropdown from it and prefills subject/body on selection.
 *
 * When fetched WITH an order context, `subject`/`body` come back already
 * rendered against that order's real variables. Without an order, they carry
 * the raw {token} template strings. `custom` is always blank.
 */
export interface EmailTemplate {
  key: string;
  label: string;
  subject: string;
  body: string;
  attachInvoice: boolean;
  payLink: boolean;
}

export interface EmailTemplatesResponse {
  templates: EmailTemplate[];
  /** Resolved variable palette (only populated when an order context is given). */
  variables: Record<string, string>;
}

/**
 * Fetch the Send-Email template catalog. Pass the Booqable order id to get
 * subject/body rendered against the order's real variables.
 */
export const fetchEmailTemplates = async (
  orderId?: string | null,
): Promise<EmailTemplatesResponse> => {
  const qs = orderId ? `?order_id=${encodeURIComponent(orderId)}` : '';
  const response = await apiFetch(`/emails/templates${qs}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch email templates: ${response.statusText}`);
  }
  const data = await response.json();
  return {
    templates: data.templates || [],
    variables: data.variables || {},
  };
};

/** Payload for saving an edited template (#20, Option B). */
export interface SaveEmailTemplateInput {
  key: string;
  subject: string;
  body: string;
  lang?: string;
}

/**
 * Save edited subject/body for one Send-Email template. Upserts the override
 * into the DB (code stays the canonical fallback). `custom` is not editable.
 */
export const saveEmailTemplate = async (
  input: SaveEmailTemplateInput,
): Promise<void> => {
  const { key, ...rest } = input;
  const response = await apiFetch(`/emails/templates/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rest),
  });
  if (!response.ok) {
    let message = `Failed to save template: ${response.statusText}`;
    try {
      const data = await response.json();
      if (data?.error) message = data.error;
    } catch {
      /* keep default message */
    }
    throw new Error(message);
  }
};

/** Tokens the templates can substitute, shown as a palette in the editor. */
export const EMAIL_TEMPLATE_TOKENS: string[] = [
  '{customerName}',
  '{orderNumber}',
  '{invoiceNumber}',
  '{totalAmount}',
  '{balanceDue}',
  '{dueDate}',
  '{originalDate}',
  '{newDate}',
  '{extensionFee}',
  '{payLink}',
  '{payButton}',
  '{companyName}',
];
